import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Avatar from 'react-avatar'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import AuthAvatar from '../../../../../UI/AuthAvatar'
import http from '../../../../../shared/http'
import { TApi, TRoutes } from '../../../../../shared/const'
import { stateLabel } from '../../../../../shared/helpers'

import styles from './expertsFlow.module.scss'

/**
 * Der Stand dieser Junior Company im Verzeichnis der Unterstuetzer:innen:
 * verbundene und passende Personen — dieselben beiden Listen, die die
 * Schueler:innen sehen (`views/common/SupportersList`), nur ohne
 * Anfrage-Knopf. Vorher musste die Verwaltung dafuer in die
 * Unterstuetzer:innen-Liste wechseln und von Hand nachbauen, was die Klasse
 * angezeigt bekommt.
 *
 * Steht seit 08.08.2026 im Abschnitt „Wirtschaftsexperten" und nicht mehr in
 * der Kurzanleitung: Dort las sich „Verbundene Expert:innen (3)" neben dem
 * Abschnitt „Wirtschaftsexperten (1)" wie ein Widerspruch. Es sind zwei
 * getrennte Verzeichnisse — nebeneinander ist das zu sehen, in zwei
 * Menuepunkten war es das nicht.
 */

// Wie viele passende Personen gezeigt werden. Es ist keine Arbeitsliste,
// sondern eine Antwort auf „gaebe es ueberhaupt jemanden" — die vollstaendige
// Liste steht im Modul Unterstuetzer:innen.
const MATCHING_LIMIT = 12

export default function SupporterLists({ companyId, listUrl }) {
	const { t } = useTranslation()

	const [lists, setLists] = useState({ connected: null, matching: null })
	const [isLoading, setLoading] = useState(false)
	const [hasFailed, setFailed] = useState(false)

	useEffect(() => {
		if (!companyId) return

		let active = true
		setLoading(true)
		setFailed(false)

		Promise.all([
			// `disableToast`: Der Abschnitt sagt es selbst an der Stelle, an der die
			// Liste stuende — eine Meldung am Bildrand waere nach dem Wegklicken weg.
			http.get(listUrl(companyId), {
				params: { tab: 'connected', limit: 50 },
				disableToast: true
			}),
			http.get(listUrl(companyId), {
				params: { tab: 'matching', limit: MATCHING_LIMIT },
				disableToast: true
			})
		])
			.then(([connected, matching]) => {
				if (!active) return
				setLists({ connected: connected.data, matching: matching.data })
			})
			.catch(() => {
				if (active) setFailed(true)
			})
			.finally(() => {
				if (active) setLoading(false)
			})

		return () => {
			active = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId])

	/*
	 * Eine Person als Kachel. Bewusst ohne Klickziel: Das Profil gehoert ins
	 * Modul Unterstuetzer:innen, und ein Fenster, das hier nur die halbe
	 * Wahrheit zeigte, waere schlechter als der Verweis darauf.
	 */
	const personCard = person => (
		<article key={person.id} className={styles.person}>
			<AuthAvatar
				hashName={person.avatarHashName}
				className={styles.personAvatar}
				fallback={<Avatar name={person.displayName || '?'} size="40" round />}
			/>
			<div className={styles.personBody}>
				<span className={styles.personName}>{person.displayName}</span>
				{Boolean(person.company || person.position) && (
					<span className={styles.personMeta}>
						{[person.position, person.company].filter(Boolean).join(' · ')}
					</span>
				)}
				{/* Nur wenn eines hinterlegt ist — eine leere Zeile sieht aus wie ein
				    Fehler, nicht wie eine fehlende Angabe. */}
				{Boolean(stateLabel(person.state)) && (
					<span className={styles.personMeta}>{stateLabel(person.state)}</span>
				)}
				{Boolean(person.competences?.length) && (
					<div className={styles.personTags}>
						{person.competences.slice(0, 3).map(item => (
							<span key={item} className={styles.personTag}>
								{item}
							</span>
						))}
					</div>
				)}
				{/*
				 * Woran es an dieser Junior Company scheitert. Steht auch an einer
				 * passenden Person nicht im Weg: Wer nichts hinterlegt hat, hat auch
				 * keinen Befund.
				 */}
				{Boolean(person.matchMisses?.length) && (
					<p className={styles.personMismatch}>
						{t('doesNotMatch', {
							criteria: person.matchMisses.map(key => t(`matchCriterion${key}`)).join(', ')
						})}
					</p>
				)}
			</div>
			{person.connectionStatus && (
				<span className={clsx(styles.connection, styles[`connection${person.connectionStatus}`])}>
					{t(`connectionStatus${person.connectionStatus}`)}
				</span>
			)}
		</article>
	)

	const personSection = (titleKey, hintKey, data, emptyKey) => (
		<section className={styles.peopleBlock}>
			<div className={styles.peopleHead}>
				<h4 className={styles.peopleTitle}>
					{t(titleKey)}{' '}
					{typeof data?.count === 'number' && <span className={styles.peopleCount}>({data.count})</span>}
				</h4>
				<p className={styles.peopleHint}>{t(hintKey)}</p>
			</div>

			{isLoading && !data ? (
				<p className={styles.peopleNote}>{t('Loading')}</p>
			) : data?.rows?.length ? (
				<>
					<div className={styles.peopleGrid}>{data.rows.map(personCard)}</div>
					{/* Nicht stillschweigend abschneiden: Wer mehr sucht, soll wissen,
					    dass es mehr gibt und wo. */}
					{data.count > data.rows.length && (
						<p className={styles.peopleNote}>
							{/* `rest` statt `count`: i18next 20 wuerde bei `count` eine
						    Mehrzahlform suchen, die es hier nicht gibt. */}
							{t('More in the supporters module', { rest: data.count - data.rows.length })}{' '}
							<Link className={styles.inlineLink} to={TRoutes.SUPPORTERS}>
								{t('supporters')}
							</Link>
							{'.'}
						</p>
					)}
				</>
			) : (
				<p className={styles.peopleNote}>{t(emptyKey)}</p>
			)}
		</section>
	)

	if (hasFailed) return <p className={styles.peopleNote}>{t('Experts could not be loaded')}</p>

	return (
		<>
			{personSection('Connected experts', 'Connected experts hint', lists.connected, 'No connected experts yet')}
			{personSection('Matching supporters', 'Matching experts hint', lists.matching, 'noSupportersFound')}
		</>
	)
}

SupporterLists.defaultProps = {
	listUrl: TApi.ADMIN_GET_COMPANY_SUPPORTERS
}

SupporterLists.propTypes = {
	companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	// Bekommt die Kennung der Junior Company und gibt die Adresse zurueck; die
	// Lehrkraft fragt ihre eigene (siehe views/teacher/CompanyProfile).
	listUrl: PropTypes.func
}
